/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.15.0
 * @since 1.14.1
 */

import { Fragment, useCallback, useMemo } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { EmailInput } from '../../../components/common/EmailInput/EmailInput';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardBillsDoc,
} from '../../../modules/internationalization/intSelectors';
import { IWizardBillsDocTranslation } from '../../../modules/internationalization/types';
import ICommonTranslation from '../../../modules/internationalization/types/common';
import { WizardReportHeaderStyled } from '../../../modules/wizard/components/WizardReport/styled/WizardReportStyled';
import WizardReport from '../../../modules/wizard/components/WizardReport/WizardReport';
import { EWizardReportParam } from '../../../modules/wizard/components/WizardReport/WizardReportConstants';
import { WizardReportParam } from '../../../modules/wizard/components/WizardReport/WizardReportParam';
import { makeSelectWizardReportParam } from '../../../modules/wizard/components/WizardReport/wizardReportSelectors';
import { IWizardReportParam } from '../../../modules/wizard/components/WizardReport/wizardReportTypes';
import { IWizardSummaryParam, WizardSummary } from '../../../modules/wizard/components/WizardSummary/WizardSummary';
import { wizardReportSetParamAction } from '../../../modules/wizard/wizardActions';
import { IWizardReportSetParamPayload } from '../../../modules/wizard/wizardTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { IWizardBillsDocReducer } from '../wizardBillsDocReducer';
import { makeSelectWizardBillsDocParam } from '../wizardBillsDocSelectors';
import { WizardBlockDocStepConfirmationStyled } from './styled/WizardBlockDocStyled';

const WizardBillsDocStepConfirmation = ({
                                            formatChoose,
                                            isActOrderChecked,
                                            isInvoiceChecked,
                                            isInvoiceOrderChecked,
                                            isOrderChecked,
                                            isOrderWithDetailChecked,
                                            reportDestEmail,
                                            translationBillsDoc,
                                            translationCommon,
                                            setNotificationParam,
                                        }: IConnectedProps & IDispatchProps) => {
    const handleChange = useCallback((payload: IWizardReportSetParamPayload) => {
        setNotificationParam(payload);
    }, [setNotificationParam]);

    const billsDocSummaryParams: IWizardSummaryParam[] = useMemo(() => [
        {
            title: translationBillsDoc.summaryParams.documents,
            values: [
                isOrderChecked
                    ? `${isOrderWithDetailChecked
                        ? translationBillsDoc.chooseDoc.reportWithDetail
                        : translationBillsDoc.chooseDoc.reportWithoutDetail}, ${formatChoose}`
                    : '',
                isInvoiceChecked ? `${translationBillsDoc.chooseDoc.invoice}, PDF` : '',
                isInvoiceOrderChecked ? `${translationBillsDoc.chooseDoc.invoiceOrder}, PDF` : '',
                isActOrderChecked ? `${translationBillsDoc.chooseDoc.actOrder}, PDF` : '',
            ],
        },
    ], [
        formatChoose,
        isActOrderChecked,
        isInvoiceChecked,
        isInvoiceOrderChecked,
        isOrderChecked,
        isOrderWithDetailChecked,
        translationBillsDoc,
    ]);

    return (
        <WizardBlockDocStepConfirmationStyled>
            <WizardReport
                notificationValidate={() => {
                }}
            >
                <Fragment>
                    <WizardReportHeaderStyled>
                        {translationBillsDoc.sendDocumentTitle}
                    </WizardReportHeaderStyled>

                    <WizardReportParam
                        dataName="wizard-report-param-email"
                        isEnabled={reportDestEmail.isEnabled}
                        label={translationBillsDoc.reports.destEmailForLink}
                        setEnabled={(isEnabled: boolean) => handleChange({
                            paramType: EWizardReportParam.EMAIL_DEST, isEnabled,
                        })}
                    >
                        <EmailInput
                            dataName="wizard-report-param-email-input"
                            emailInputValue={reportDestEmail.value}
                            isValid={reportDestEmail.isValid}
                            translation={translationCommon}
                            onEmailInputChange={(value: string) => handleChange({
                                paramType: EWizardReportParam.EMAIL_DEST, value,
                            })}
                            onEmailInputValidate={(isValid: boolean) => handleChange({
                                paramType: EWizardReportParam.EMAIL_DEST, isValid,
                            })}
                        />
                    </WizardReportParam>
                </Fragment>
            </WizardReport>
            <div>
                <WizardSummary params={billsDocSummaryParams}/>
            </div>
        </WizardBlockDocStepConfirmationStyled>
    );
};

type TParams = Pick<IWizardBillsDocReducer,
    | 'formatChoose'
    | 'isActOrderChecked'
    | 'isInvoiceChecked'
    | 'isInvoiceOrderChecked'
    | 'isOrderChecked'
    | 'isOrderWithDetailChecked'>;

export interface IConnectedProps extends TParams {
    reportDestEmail: IWizardReportParam;
    translationBillsDoc: IWizardBillsDocTranslation;
    translationCommon: ICommonTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    formatChoose: makeSelectWizardBillsDocParam('formatChoose'),
    isActOrderChecked: makeSelectWizardBillsDocParam('isActOrderChecked'),
    isInvoiceChecked: makeSelectWizardBillsDocParam('isInvoiceChecked'),
    isInvoiceOrderChecked: makeSelectWizardBillsDocParam('isInvoiceOrderChecked'),
    isOrderChecked: makeSelectWizardBillsDocParam('isOrderChecked'),
    isOrderWithDetailChecked: makeSelectWizardBillsDocParam('isOrderWithDetailChecked'),

    reportDestEmail: makeSelectWizardReportParam(EWizardReportParam.EMAIL_DEST),
    translationBillsDoc: makeSelectTranslationWizardBillsDoc(),
    translationCommon: makeSelectTranslationCommon(),
});

export interface IDispatchProps {
    setNotificationParam: (payload: IWizardReportSetParamPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setNotificationParam: payload => dispatch(wizardReportSetParamAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardBillsDocStepConfirmation);
